import { type JSX } from 'react';
import { NextPage } from 'next';
import { v4 as uuidv4 } from 'uuid';
import { Toaster } from 'react-hot-toast';
import { MemrisePageContext } from 'src/utils/context';
import { MemriseError } from 'src/utils/errors';
import { ParseIntError, safeParseInt } from 'src/utils/numbers';
import { SessionSourceType, WebSessionType } from 'src/api/learningSessions/types';
import { BlobPronunciationQueue } from 'src/apps/Path/BlobPronunciationQueue';
import { slugToSessionType } from '../../sessionConfig';
import SessionContent from '../SessionContent';

interface SessionProps {
  sessionType: WebSessionType;
  sessionSourceType: SessionSourceType;
  sessionSourceId: number;
  sessionId: string;
  refreshKey: string;
}

export interface SessionContext extends MemrisePageContext {
  query: {
    sessionSlug: string;
    scenario_id?: string;
    language_pair_id?: string;
    blob_id?: string;
    /** Used to force-refresh components when we cannot transition to a new page */
    key: string;
    source_element?: string;
  };
}

const getSessionSourceType = ({
  blob_id,
  scenario_id,
}: SessionContext['query']): SessionSourceType => {
  if (blob_id) return 'user_path_blob';
  if (scenario_id) return 'scenario';
  return 'language_pair';
};

/* istanbul ignore next: TS ensures we always return a session type */
const Session: NextPage<SessionProps> = ({
  sessionType,
  sessionSourceType,
  refreshKey,
  ...props
}): JSX.Element => {
  return (
    <>
      <Toaster position="top-center" />
      <BlobPronunciationQueue
        key={`${props.sessionId}:${refreshKey}`}
        sessionType={sessionType}
        sessionSourceType={sessionSourceType}
        sessionSourceId={props.sessionSourceId}
      >
        <SessionContent
          key={refreshKey}
          sessionType={sessionType}
          sessionSourceType={sessionSourceType}
          {...props}
        />
      </BlobPronunciationQueue>
    </>
  );
};

Session.getInitialProps = ({ query }: SessionContext): SessionProps => {
  const { sessionSlug, key, scenario_id, language_pair_id, blob_id } = query;

  const sessionType = slugToSessionType(sessionSlug);

  if (sessionType === null) throw new MemriseError('Page not found', 404);

  // A blob session also carries its language pair id, which is not its source.
  const sessionSourceIdParam = blob_id || language_pair_id || scenario_id;

  if (!sessionSourceIdParam) {
    throw new MemriseError('Please provide a valid scenario or language pair id', 400);
  }

  const sessionSourceId = safeParseInt(sessionSourceIdParam);

  if (sessionSourceId instanceof ParseIntError) {
    throw new MemriseError('Please provide a valid scenario or language pair ids', 400);
  }

  const sessionSourceType: SessionSourceType = getSessionSourceType(query);

  const sessionId = uuidv4();

  return {
    sessionType,
    sessionSourceType,
    sessionSourceId,
    sessionId,
    refreshKey: key || '',
  };
};

export default Session;
